import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Flex, 
  Card, 
  Text, 
  Button, 
  Heading, 
  Container, 
  Grid,
  Badge,
  AlertDialog,
  IconButton
} from '@radix-ui/themes';
import { PlusIcon, PersonIcon, QuestionMarkCircledIcon, TrashIcon } from '@radix-ui/react-icons';
import './Classes.css';
import TopBar from '../../../components/ui/TopBar/TopBar';
import { API_ENDPOINTS } from '../../../config/api.config';
import { toggleEnvironment } from '../../../utils/apiClient';
import { api, getAuthHeaders } from '../../../utils/api';
import useUserRole from '../../../hooks/useUserRole';
import { useTrialContext } from '../../../contexts/TrialContext';
import refreshToken from '../../../utils/apiUtils';

const TeacherClasses = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const navigate = useNavigate();
  const { role, isLoading: isRoleLoading } = useUserRole();
  const { checkLimit, trialStatus } = useTrialContext();
  
  // Get user account type from localStorage
  const userAccount = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const isTrialAccount = userAccount?.accountType === 'trial' || trialStatus?.isTrial;
  // Only show warning if this is a trial/free account AND has reached the limit (2 classes for free plan)
  const isClassLimitReached = isTrialAccount && classes.length >= 2;

  const makeRequest = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token available');
    }

    try {
      const response = await axios.get(
        '/api/v1/stem@home/teacher/classes',
        {
          baseURL: toggleEnvironment(),
          headers: {
            ...getAuthHeaders(),
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response;
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshToken();
        if (newToken) {
          const retryResponse = await axios.get(
            '/api/v1/stem@home/teacher/classes',
            {
              baseURL: toggleEnvironment(),
              headers: {
                ...getAuthHeaders(),
                'Authorization': `Bearer ${newToken}`
              }
            }
          );
          return retryResponse;
        }
        throw new Error('Token refresh failed');
      }
      throw err;
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      console.log('Attempting to fetch classes, attempt:', retryCount + 1);
      
      // Wait for role to be loaded
      if (isRoleLoading) {
        console.log('Role is still loading...');
        return;
      }

      // Verify role is correct
      if (role !== 'teacher') {
        console.log('User is not a teacher, redirecting...');
        navigate('/dashboard');
        return;
      }

      // Check if we have user data
      const userData = localStorage.getItem('user');
      if (!userData) {
        console.log('No user data found, redirecting to login...');
        navigate('/login');
        return;
      }

      const response = await makeRequest();

      if (response.data.success === false) {
        throw new Error(response.data.message || 'Failed to fetch classes');
      }

      console.log('Classes fetched successfully:', response.data);
      setClasses(response.data.data || []);
      setError(null);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error("Error fetching classes:", err);
      setError("Failed to load classes");
      
      if (err.message === 'Token refresh failed' || err.message === 'No token available') {
        navigate('/login');
        return;
      }

      // Retry logic
      if (retryCount < 3) { // Maximum 3 retries
        console.log('Scheduling retry...');
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 1000); // Wait 1 second before retrying
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, retryCount, role, isRoleLoading, makeRequest]);

  // Initial fetch on mount
  useEffect(() => {
    let mounted = true;
    
    if (mounted) {
      setLoading(true);
      fetchClasses();
    }

    return () => {
      mounted = false;
    };
  }, [fetchClasses]);

  // Refetch when retry count changes
  useEffect(() => {
    if (retryCount > 0) {
      fetchClasses();
    }
  }, [retryCount, fetchClasses]);



  if (isRoleLoading || loading) {
    return (
      <>
        <TopBar 
          title="Classes"
          showActions={true} 
        />
        <Box className="p-4 md:p-8 w-full min-h-screen" style={{ paddingTop: '80px' }}>
        <Flex justify="end" align="center" className="mb-4 md:mb-6">
          <Button 
            size="3"
            variant="solid"
            className="bg-black text-white hover:bg-gray-800 px-4 md:px-6"
            disabled
          >
            <PlusIcon />
            <Text className="hidden sm:inline ml-2">Add New Class</Text>
            <Text className="sm:hidden ml-2">Add Class</Text>
          </Button>
        </Flex>
        <Box className="bg-white rounded-lg p-6 md:p-12 shadow-sm text-center">
          <Text size="4" className="text-gray-500">
            {isRoleLoading ? 'Loading user data...' : 'Loading classes...'}
            {(!isRoleLoading && retryCount > 0) && ` (Attempt ${retryCount + 1})`}
          </Text>
        </Box>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar 
          title="Classes"
          showActions={true} 
        />
        <Box className="p-4 md:p-8 w-full min-h-screen" style={{ paddingTop: '80px' }}>
        <Flex justify="end" align="center" className="mb-4 md:mb-6">
          <Button 
            size="3"
            variant="solid"
            className="bg-black text-white hover:bg-gray-800 px-4 md:px-6"
            onClick={() => navigate('/create-class')}
          >
            <PlusIcon />
            <Text className="hidden sm:inline ml-2">Add New Class</Text>
            <Text className="sm:hidden ml-2">Add Class</Text>
          </Button>
        </Flex>
        <Box className="bg-white rounded-lg p-6 md:p-12 shadow-sm text-center">
          <Text size="8" className="block mb-4">❌</Text>
          <Text size="5" className="block mb-6 text-red-600">
            {error}
            {retryCount < 3 && ' (Retrying...)'}
          </Text>
        </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <TopBar 
        title={`Classes (${classes.length})`}
        showActions={true} 
      />
      <Box className="p-4 md:p-8 w-full min-h-screen" style={{ paddingTop: '80px' }}>
        {isClassLimitReached && (
          <Box className="bg-orange-100 border border-orange-300 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <Flex align="center" justify="center" gap="2">
              <Badge color="orange" size="2" className="md:size-3">
                ⚠️ FREE PLAN LIMIT REACHED
              </Badge>
            </Flex>
          </Box>
        )}
      
      {/* Add Class Button */}
      <Flex justify="end" align="center" className="mb-4 md:mb-6">
        <Button 
          size="3"
          variant="solid"
          className="bg-black text-white hover:bg-gray-800 px-4 md:px-6"
          onClick={() => navigate('/create-class')}
          disabled={isClassLimitReached}
          style={isClassLimitReached ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <PlusIcon />
          <Text className="hidden sm:inline ml-2">Add New Class</Text>
          <Text className="sm:hidden ml-2">Add Class</Text>
        </Button>
      </Flex>
        
      {/* Free plan limit warning message */}
      {isClassLimitReached && (
        <Box className="bg-red-500 text-white p-3 md:p-4 mb-4 md:mb-6 rounded font-bold text-center">
          <Text size="3" className="md:size-4">
            ⚠️ <strong>FREE PLAN LIMIT REACHED:</strong> You cannot create more than 2 classes with your free account
          </Text>
        </Box>
      )}

      {classes.length === 0 ? (
        <Box 
          className="bg-white rounded-lg p-6 md:p-12 shadow-sm"
          style={{ 
            width: '100%', 
            minWidth: '500px', 
            maxWidth: '800px', 
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Flex direction="column" align="center" justify="center" gap="4" style={{ width: '100%', minWidth: '400px' }}>
            <Text size="8" className="text-center">📚</Text>
            <div
              style={{ 
                fontSize: '1.125rem',
                color: '#6B7280',
                whiteSpace: 'normal', 
                wordWrap: 'break-word', 
                overflowWrap: 'break-word',
                minWidth: '300px',
                maxWidth: '100%',
                width: 'auto',
                display: 'block',
                textAlign: 'center',
                lineHeight: '1.5',
                marginBottom: '1rem',
                marginTop: '1rem'
              }}
            >
              You haven't created any classes yet
            </div>
            <Button 
              size="3"
              variant="solid"
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => navigate('/create-class')}
              disabled={isClassLimitReached}
            >
              Create Your First Class
            </Button>
          </Flex>
        </Box>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          {classes.map((classItem) => (
            <Card 
              key={classItem._id} 
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onClick={() => navigate(`/class/${classItem._id}`)}
            >
              <Flex direction="column" gap="3" p="4">
                <Heading size="4" className="truncate" title={classItem.name}>
                  {classItem.name}
                </Heading>
                <Flex direction="column" gap="2">
                  <Flex align="center" justify="between">
                    <Flex align="center" gap="2" className="min-w-0 flex-1">
                      <PersonIcon className="flex-shrink-0" />
                      <Text size="2" color="gray" className="truncate">Students:</Text>
                    </Flex>
                    <Text size="2" color="gray" className="flex-shrink-0">
                      {classItem.students?.length || 0}
                    </Text>
                  </Flex>
                  <Flex align="center" justify="between">
                    <Flex align="center" gap="2" className="min-w-0 flex-1">
                      <QuestionMarkCircledIcon className="flex-shrink-0" />
                      <Text size="2" color="gray" className="truncate">Questions:</Text>
                    </Flex>
                    <Text size="2" color="gray" className="flex-shrink-0">
                      {classItem.questions?.length || 0}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))}
        </div>
      )}
      </Box>
    </>
  );
};

export default TeacherClasses; 